import {
  Component,
  OnInit,
  OnDestroy,
  Output,
  EventEmitter,
  Input,
  OnChanges,
  SimpleChanges, ChangeDetectorRef,
} from '@angular/core';
import { FormControl, FormGroup } from '@angular/forms';
import { User } from '../../models/user.model';
import { GraphService } from '../../services/graph.service';
import { AppSettings, Settings } from '../../../app.settings';
import { TerritoryService } from '../../services/territory-management.service';
import { UserAuthService } from '../../../shared/services/user-auth.service';
import {TitleService} from '../../services/title.service';
import { MsgBannerService } from '../../../shared/components/msg-banner/services/msg-banner.service';
import { firstValueFrom, ReplaySubject, Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { TelemetryService } from 'src/app/shared/services/telemetry.service';
import {OfflineService} from '../../../shared/services/offline.service';
import { ScreenSizeService } from '../../services/screen-size.service';
import { NavigationEnd, Router } from '@angular/router';

@Component({
    selector: 'ag-top-toolbar',
    templateUrl: './top-toolbar.component.html',
    styleUrls: ['./top-toolbar.component.scss'],
    standalone: false
})
export class TopToolbarComponent implements OnInit, OnChanges, OnDestroy {
  @Input() showMenuDesktop = false;
  @Input() hideMenuIcon = false;
  @Input() hideTerritorySelect = false;
  @Input() type = 'user';
  @Output() toggleMenu: EventEmitter<boolean> = new EventEmitter<boolean>(
    false
  );
  public settings: Settings;
  toggled: boolean = false;
  isMobile: boolean = window.innerWidth <= 960;
  public user: User;
  territory: number;
  territories: number[] = [];
  territoryFilterCtrl: FormControl = new FormControl('');
  filteredTerritories: ReplaySubject<number[]> = new ReplaySubject<number[]>(1);
  title: string = 'MyTeam';
  form: FormGroup;
  private readonly onDestroy = new Subject<void>();

  get displayTitle(): string {
    return this.isMobile ? this.getMobileTitle(this.title) : this.title;
  }

  get displayUserName(): string | null {
    return this.userAuthService.hasADCredentials() ? null : this.user?.displayName;
  }

  get showSurveyDetailsBackButton(): boolean {
    return this.router.url.startsWith('/manage-survey/survey-details');
  }

  get isSurveyDetailsRoute(): boolean {
    return this.router.url.startsWith('/store-survey/survey-details')
      || this.router.url.startsWith('/manage-survey/survey-details');
  }

  get brandRouterLink(): string | null {
    return this.router.url.startsWith('/manage-survey') ? null : '/';
  }

  constructor(
    private readonly graphService: GraphService,
    private readonly userAuthService: UserAuthService,
    private readonly appSettings: AppSettings,
    private readonly territoryService: TerritoryService,
    private readonly titleService: TitleService,
    private readonly telemetryService: TelemetryService,
    private readonly changeDetector: ChangeDetectorRef,
    private readonly router: Router,
    private readonly msgBanner: MsgBannerService,
    private readonly offlineService: OfflineService,
    private readonly screenSizeService: ScreenSizeService
  ) {
    this.settings = appSettings.settings;
  }

  ngOnChanges(changes: SimpleChanges): void {}

  ngOnInit(): void {
    this.user = this.graphService.user;
    this.territory = this.territoryService.getTerritory();
    this.form = new FormGroup({
      terr: new FormControl(this.territory),
    });
    void this.loadTerritories();
    this.territoryFilterCtrl.valueChanges
      .pipe(takeUntil(this.onDestroy))
      .subscribe(() => this.filterTerritories());
    this.screenSizeService.isSmall$
      .pipe(takeUntil(this.onDestroy))
      .subscribe(isMobile => {
        this.isMobile = isMobile;
      });
    this.titleService.titleSubject.subscribe(title => {
      if (title) {
        this.title = title;
        this.changeDetector.detectChanges();
      }
    });

    this.router.events
      .pipe(takeUntil(this.onDestroy))
      .subscribe(event => {
        if (event instanceof NavigationEnd) {
          this.changeDetector.detectChanges();
        }
      });
  }

  ngOnDestroy(): void {
    this.onDestroy.next();
    this.onDestroy.complete();
  }

  async loadTerritories(): Promise<void> {
    try {
      this.territories = await firstValueFrom(
        this.territoryService.getUserAssignedTerritories()
      );
      this.filteredTerritories.next(this.territories.slice());
      this.changeDetector.detectChanges();
    } catch (error) {
      console.error('Error loading territories', error);
      this.msgBanner.addMsgError(
        'An error has occurred. Please contact your administrator.'
      );
    }
  }

  protected filterTerritories(): void {
    if (!this.territories) {
      return;
    }
    const search = (this.territoryFilterCtrl.value ?? '').toString().trim();
    if (!search) {
      this.filteredTerritories.next(this.territories.slice());
      return;
    }
    this.filteredTerritories.next(
      this.territories.filter(
        terr => terr.toString().indexOf(search) > -1
      )
    );
  }

  toggle() {
    this.toggled = !this.toggled;
    this.toggleMenu.emit(this.toggled);
  }

  goBackToManageSurvey(): void {
    void this.router.navigateByUrl('/manage-survey');
  }

  getProfilePhoto() {
    return this.graphService.user.profilePhotoUrl
      ? this.graphService.user.profilePhotoUrl
      : 'assets/images/hero/user.png';
  }

  logout() {
    this.telemetryService.trackAuthLogout(this.userAuthService.getUsername());
    this.userAuthService.logout();
  }

  private getMobileTitle(title: string): string {
    const normalizedTitle = (title ?? '')
      .replace(/-/g, ' ')
      .replace(/\s+/g, ' ')
      .trim()
      .toLowerCase();

    switch (normalizedTitle) {
      case 'serviced stores':
        return 'Serviced';
      case 'non serviced stores':
        return 'Non Serviced';
      case 'service records':
        return 'Records';
      default:
        return title;
    }
  }

  async onTerritoryChange(territory: number): Promise<void> {
    if (!territory || territory === this.territoryService.getTerritory()) {
      return;
    }
    try {
      await firstValueFrom(
        this.territoryService.updateSelectedTerritory(territory)
      );
      this.territoryService.saveTerritoryNumber(territory);
      this.territory = territory;
      await this.offlineService.purgeDraftsOnTerritoryChange();
      globalThis.location.reload();
    } catch (error: any) {
      this.territory = this.territoryService.getTerritory();
      if (error.status === 404) {
        this.msgBanner.addMsgError(error.error.message);
      } else {
        this.msgBanner.addMsgError(
          'An error has occurred. Please contact your administrator.'
        );
      }
    }
  }
}
